import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

export default function SleepCalc() {
  const [tab, setTab] = useState('cycles');
  return (
    <CalcShell look="cnet" title="Sleep Calculator" subtitle="Optimal bed/wake times based on 90-minute sleep cycles, plus a Sleep Length calculator to hit a target sleep duration.">
      <div className="flex gap-2 mb-0">
        {[['cycles', 'Bed/Wake by Sleep Cycles'], ['length', 'Sleep Length Calculator']].map(([v, l]) => (
          <button key={v} data-testid={`sleep-tab-${v}`}
            className={`cnet-tab ${tab === v ? 'active' : ''}`}
            onClick={() => setTab(v)}>{l}</button>
        ))}
      </div>
      <div className="cnet-tab-body">
        {tab === 'cycles' ? <CyclesSection /> : <LengthSection />}
      </div>
    </CalcShell>
  );
}

function CyclesSection() {
  const [wakeTime, setWakeTime] = useState('07:00');
  const [bedTime, setBedTime] = useState('23:00');
  const [mode, setMode] = useState('wake');
  const cycles = 90; // minutes per sleep cycle
  const falling = 14; // minutes to fall asleep

  const c = useMemo(() => {
    const [h, m] = (mode === 'wake' ? wakeTime : bedTime).split(':').map(Number);
    const base = new Date(); base.setHours(h, m, 0, 0);
    const results = [];
    for (let i = 6; i >= 3; i--) {
      const t = new Date(base);
      const offset = i * cycles + falling;
      if (mode === 'wake') t.setMinutes(t.getMinutes() - offset);
      else t.setMinutes(t.getMinutes() + offset);
      results.push({ cycles: i, time: t, hours: (i * cycles / 60).toFixed(1) });
    }
    return results;
  }, [wakeTime, bedTime, mode]);

  const fmt = d => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  return (
    <>
      <div className="flex gap-2 mb-4">
        <button onClick={() => setMode('wake')} className={`px-4 py-2 rounded-full text-sm font-semibold ${mode === 'wake' ? 'bg-blue-600 text-white' : 'bg-slate-100'}`}>Wake up at…</button>
        <button onClick={() => setMode('bed')} className={`px-4 py-2 rounded-full text-sm font-semibold ${mode === 'bed' ? 'bg-blue-600 text-white' : 'bg-slate-100'}`}>Sleep now…</button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>{mode === 'wake' ? 'Wake Time' : 'Bedtime'}</label>
            <input type="time" value={mode === 'wake' ? wakeTime : bedTime} onChange={e => mode === 'wake' ? setWakeTime(e.target.value) : setBedTime(e.target.value)} data-testid="sleep-cycle-time" />
          </div>
        </div>
        <div>
          <h3 className="font-bold mb-2">Recommended {mode === 'wake' ? 'bedtimes' : 'wake times'}</h3>
          <table className="data-table" data-testid="sleep-cycle-table"><thead><tr><th>Time</th><th>Cycles</th><th>Sleep</th></tr></thead><tbody>
            {c.map(r => <tr key={r.cycles}><td className="font-semibold">{fmt(r.time)}</td><td>{r.cycles}</td><td>{r.hours}h</td></tr>)}
          </tbody></table>
        </div>
      </div>
    </>
  );
}

function LengthSection() {
  // Matches the Sleep Length Calculator on Calculator.net
  const [mode, setMode] = useState('wake'); // wake | bed
  const [hour, setHour] = useState(6);
  const [minute, setMinute] = useState(0);
  const [ampm, setAmpm] = useState('AM');
  const [sleepH, setSleepH] = useState(7);
  const [sleepM, setSleepM] = useState(30);

  const result = useMemo(() => {
    let h24 = Number(hour) % 12; if (ampm === 'PM') h24 += 12;
    const base = new Date(); base.setHours(h24, Number(minute), 0, 0);
    const totalMin = Number(sleepH) * 60 + Number(sleepM);
    const out = new Date(base);
    if (mode === 'wake') out.setMinutes(out.getMinutes() - totalMin);
    else out.setMinutes(out.getMinutes() + totalMin);
    return out;
  }, [mode, hour, minute, ampm, sleepH, sleepM]);

  const fmt = d => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  return (
    <>
      <p className="text-sm text-slate-700 mb-4">Use this calculator to compute what time to wake up or go to bed to get a given number of hours of sleep. When counting, please deduct the time taken to fall asleep, which can be very different for different people.</p>
      <div className="cnet-panel">
        <div className="mb-3">
          <div className="mb-1"><b>I want to</b></div>
          <label className="flex items-center gap-2 mb-1"><input type="radio" name="sl-mode" checked={mode === 'wake'} onChange={() => setMode('wake')} data-testid="sl-mode-wake" /> wake up at</label>
          <label className="flex items-center gap-2"><input type="radio" name="sl-mode" checked={mode === 'bed'} onChange={() => setMode('bed')} data-testid="sl-mode-bed" /> go to bed at</label>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <input data-testid="sl-hour" className="cnet-input" type="number" min="1" max="12" value={hour} onChange={e => setHour(e.target.value)} style={{ width: 90 }} />
          <span className="font-bold">:</span>
          <input data-testid="sl-min" className="cnet-input" type="number" min="0" max="59" value={minute} onChange={e => setMinute(e.target.value)} style={{ width: 90 }} />
          <select data-testid="sl-ampm" className="cnet-select" value={ampm} onChange={e => setAmpm(e.target.value)}><option>AM</option><option>PM</option></select>
        </div>
        <div><b>I want to sleep for</b></div>
        <div className="flex items-center gap-2 mt-2">
          <div className="flex items-center gap-1"><input data-testid="sl-hours" className="cnet-input" type="number" min="0" value={sleepH} onChange={e => setSleepH(e.target.value)} style={{ width: 90 }} /><span className="text-slate-500 text-sm">hours</span></div>
          <div className="flex items-center gap-1"><input data-testid="sl-mins" className="cnet-input" type="number" min="0" max="59" value={sleepM} onChange={e => setSleepM(e.target.value)} style={{ width: 90 }} /><span className="text-slate-500 text-sm">minutes</span></div>
        </div>
      </div>
      <div className="result-panel mt-4">
        <div className="result-title">{mode === 'wake' ? 'You should go to bed at' : 'You will wake up at'}</div>
        <div className="result-value" data-testid="sl-result">{fmt(result)}</div>
        <div className="text-sm text-slate-700 mt-2">Total sleep: {sleepH}h {sleepM}m</div>
      </div>
    </>
  );
}
